/**
 * API Error Boundary Component
 *
 * Specialized error boundary for handling API errors
 * Shows more specific recovery options for network/API failures
 */

import React, { Component } from 'react';
import type { ReactNode } from 'react';
import { Alert } from './ui/Alert';
import { normalizeError, getUserFriendlyMessage, getErrorSeverity, logError } from '../utils/error-handler';
import type { AppError } from '../types/errors';

interface ApiErrorBoundaryProps {
  children: ReactNode;
  onRetry?: () => void;
  resetKeys?: Array<string | number>;
}

interface ApiErrorBoundaryState {
  hasError: boolean;
  error: AppError | null;
  errorInfo: React.ErrorInfo | null;
}

export class ApiErrorBoundary extends Component<ApiErrorBoundaryProps, ApiErrorBoundaryState> {
  constructor(props: ApiErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ApiErrorBoundaryState> {
    return {
      hasError: true,
      error: normalizeError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const normalizedError = normalizeError(error);
    const severity = getErrorSeverity(normalizedError);

    // Log error with context
    logError(normalizedError, {
      componentStack: errorInfo.componentStack,
      severity,
      context: 'API Error Boundary',
    });

    // Update state
    this.setState({
      errorInfo,
    });
  }

  componentDidUpdate(prevProps: ApiErrorBoundaryProps): void {
    // Reset error boundary when resetKeys change
    if (this.state.hasError && this.props.resetKeys) {
      const hasResetKeyChanged = this.props.resetKeys.some(
        (key, index) => key !== prevProps.resetKeys?.[index]
      );

      if (hasResetKeyChanged) {
        this.reset();
      }
    }
  }

  reset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleRetry = (): void => {
    this.reset();
    this.props.onRetry?.();
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      const isNetworkError = 'offline' in this.state.error;
      const isServerError =
        'status' in this.state.error && this.state.error.status && this.state.error.status >= 500;

      return (
        <div className="p-4">
          <Alert variant="error">
            <div className="space-y-3">
              <h3 className="font-semibold">
                {isNetworkError ? 'Connection Error' : 'API Error'}
              </h3>
              <p>{getUserFriendlyMessage(this.state.error)}</p>

              {import.meta.env.DEV && this.state.errorInfo && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm font-medium">
                    Error Details (Development)
                  </summary>
                  <pre className="mt-2 text-xs overflow-auto p-2 bg-gray-100 rounded max-h-64">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}

              <div className="flex gap-2 mt-4">
                <button
                  onClick={this.handleRetry}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>

                {isServerError && (
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Reload Page
                  </button>
                )}
              </div>
            </div>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}
